import { apiFiles } from "@/api/file.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { ApiFilePathTypes } from "@/types/file.types.ts";
import { ChangeEvent, ReactNode, useRef, useState } from "react";

export interface UseFileUploadParams {
  entityId: number | undefined;
  entityType: ApiEntityTypes;
  afterSend?: (file: ApiFile) => void;
  multiple?: boolean;
}

export interface UseFileUploadResult {
  input: ReactNode;
  openPicker: () => void;
  isLoading: boolean;
}

export const useFileUpload = ({
  entityId,
  entityType,
  afterSend,
  multiple = false,
}: UseFileUploadParams): UseFileUploadResult => {
  const { handleErrors } = useErrorHandle();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const onSubmit = async (fileForSend: File) => {
    const formData = new FormData();

    formData.append("file", fileForSend);

    let path: ApiFilePathTypes;

    switch (entityType) {
      case ApiEntityTypes.applicant:
        path = ApiFilePathTypes.applicant;
        break;
      case ApiEntityTypes.candidate:
        path = ApiFilePathTypes.candidate;
        break;
      case ApiEntityTypes.employee:
        path = ApiFilePathTypes.employee;
        break;
      case ApiEntityTypes.task:
        path = ApiFilePathTypes.task;
        break;
      case ApiEntityTypes.blog_post:
        path = ApiFilePathTypes.blog_attachment;
        break;
      default:
        path = ApiFilePathTypes.other;
        break;
    }

    if (entityId && entityType !== ApiEntityTypes.blog_post) {
      formData.append("entity_id", entityId.toString());
    }
    formData.append("path", path);
    formData.append("is_public", "false");
    if (entityType === ApiEntityTypes.blog_post) {
      formData.append("generate_public_link", "false");
    }
    formData.append("save_original_name", "true");

    const { data: file, error } = await apiFiles.add(formData);

    if (file) {
      afterSend?.(file);
    } else {
      handleErrors(error);
    }
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChanged = async (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const files = Array.from(input.files ?? []);
    if (files.length === 0) return;

    setIsLoading(true);
    for (const file of files) {
      await onSubmit(file);
    }
    setIsLoading(false);
    input.value = "";
  };

  const openPicker = () => {
    fileInputRef.current?.click();
  };

  const input = (
    <input
      className={"hidden"}
      type={"file"}
      multiple={multiple}
      onChange={(event) => void handleFileChanged(event)}
      ref={fileInputRef}
    />
  );

  return { input, openPicker, isLoading };
};
